// ═══════════════════════════════════════════════════
//  CONSENTEMENT COOKIES — état partagé
//
//  Audit RGPD (24/09/2026), constat C2 : rien ne doit se charger ni s'écrire
//  avant un choix explicite, et « Tout refuser » doit réellement tout
//  arrêter (effacer les cookies déjà posés, ne rien laisser tourner).
//
//  Ce module est la seule source de vérité pour le consentement analytique :
//  CookieConsent.tsx l'écrit, AnalyticsGate.tsx (et tout composant qui
//  charge un traceur tiers) le lit et réagit à ses changements via
//  CONSENT_EVENT, sans recharger la page.
//
//  Relecture PR #123 (25/09/2026, constat B3) : la politique de
//  confidentialité et la politique de cookies promettent que le choix de
//  consentement est mémorisé 6 mois puis reproposé. Ce module horodate le
//  choix et l'expire à 6 mois, et expose un évènement pour rouvrir le
//  bandeau depuis le lien « Gérer mes cookies » du pied de page.
// ═══════════════════════════════════════════════════

export type ConsentState = {
  analytics: boolean
  marketing: boolean
}

type StoredConsent = ConsentState & { storedAt: number }

export const CONSENT_KEY = 'stock-co2-consent'

/** Évènement `window` déclenché à chaque écriture du consentement. */
export const CONSENT_EVENT = 'sco2-consent-changed'

/** 6 mois, comme annoncé par les pages légales (182 jours). */
export const CONSENT_MAX_AGE_MS = 182 * 24 * 60 * 60 * 1000

/**
 * Stockage local de l'origine d'acquisition (source publicitaire du clic,
 * src/lib/acquisition/origine.ts) : gardée seulement avec le consentement à
 * la mesure des campagnes publicitaires, effacée avec les cookies Google Ads. Défini ici pour éviter un import
 * circulaire.
 */
export const CLE_ORIGINE_ACQUISITION = 'sco2-origine-acquisition'

/** Évènement `window` qui rouvre le bandeau (lien « Gérer mes cookies »). */
export const CONSENT_REOPEN_EVENT = 'sco2-consent-reopen'

/** Fonction pure, testable sans DOM ni horloge système. */
export function isConsentExpired(storedAt: number, now: number = Date.now()): boolean {
  return now - storedAt > CONSENT_MAX_AGE_MS
}

export function getStoredConsent(): ConsentState | null {
  if (typeof window === 'undefined') return null
  try {
    const raw = localStorage.getItem(CONSENT_KEY)
    if (!raw) return null
    const parsed = JSON.parse(raw) as Partial<StoredConsent>
    if (typeof parsed.storedAt !== 'number' || isConsentExpired(parsed.storedAt)) {
      // Choix expiré (ou format ancien, sans horodatage) : on l'efface et le
      // bandeau réapparaît, comme annoncé dans /confidentialite et /cookies.
      localStorage.removeItem(CONSENT_KEY)
      return null
    }
    return { analytics: !!parsed.analytics, marketing: !!parsed.marketing }
  } catch {
    return null
  }
}

/** Enregistre le choix et prévient les composants déjà montés (sans reload). */
export function storeConsent(consent: ConsentState): void {
  if (typeof window === 'undefined') return
  try {
    const record: StoredConsent = { ...consent, storedAt: Date.now() }
    localStorage.setItem(CONSENT_KEY, JSON.stringify(record))
  } catch {
    /* localStorage indisponible (navigation privée, quota) : le choix ne
       survivra pas à la session, mais rien ne doit planter pour autant. */
  }
  window.dispatchEvent(new CustomEvent<ConsentState>(CONSENT_EVENT, { detail: consent }))
}

/**
 * Efface les cookies posés par le traçage analytique : nos propres
 * identifiants et ceux de Google Analytics / GTM. Appelé quand le visiteur
 * refuse ou retire son consentement — c'est ce qui manquait à `rejectAll`
 * (constat C2 : « Tout refuser » n'effaçait rien).
 *
 * `_sco2_fp` (empreinte d'appareil) est purgé ici pour les visiteurs qui
 * l'auraient reçu avant ce correctif : le cookie lui-même n'est plus posé
 * (voir VisitorTracker.tsx), mais un ancien visiteur peut encore le porter.
 */
export function clearAnalyticsCookies(): void {
  if (typeof document === 'undefined') return

  const names = new Set<string>(['_sco2_vid', '_sco2_fp'])
  document.cookie.split(';').forEach((c) => {
    const name = c.split('=')[0]?.trim()
    if (name && /^_ga\b|^_ga_|^_gid$|^_gat/.test(name)) names.add(name)
  })

  const hostname = typeof window !== 'undefined' ? window.location.hostname : ''
  // Domaine racine (« .stock-co2.com » pour « www.stock-co2.com ») : GA4 y pose
  // _ga et _ga_* même quand le site sert depuis un sous-domaine (audit G1).
  const racine = hostname.includes('.') ? `.${hostname.split('.').slice(-2).join('.')}` : ''
  const domains = hostname
    ? Array.from(new Set(['', hostname, `.${hostname}`, racine].filter(Boolean)))
    : ['']
  const expire = 'expires=Thu, 01 Jan 1970 00:00:00 GMT'
  for (const name of names) {
    for (const domain of domains) {
      document.cookie = `${name}=; path=/; ${domain ? `domain=${domain}; ` : ''}${expire}`
    }
  }

  try {
    sessionStorage.removeItem('_sco2_sid')
  } catch {
    /* noop */
  }
}

/**
 * Efface ce qui relève de la mesure des campagnes publicitaires (catégorie
 * « marketing » du bandeau) : cookies de Google Ads posés par le Conversion
 * Linker (_gcl_*) et source publicitaire du clic gardée en stockage local.
 * Appelé au refus ou au retrait de ce consentement.
 */
export function clearAdCookies(): void {
  if (typeof document === 'undefined') return
  const names = new Set<string>()
  document.cookie.split(';').forEach((c) => {
    const name = c.split('=')[0]?.trim()
    if (name && /^_gcl_/.test(name)) names.add(name)
  })
  const hostname = typeof window !== 'undefined' ? window.location.hostname : ''
  const racine = hostname.includes('.') ? `.${hostname.split('.').slice(-2).join('.')}` : ''
  const domains = hostname ? Array.from(new Set(['', hostname, `.${hostname}`, racine].filter(Boolean))) : ['']
  const expire = 'expires=Thu, 01 Jan 1970 00:00:00 GMT'
  for (const name of names) {
    for (const domain of domains) {
      document.cookie = `${name}=; path=/; ${domain ? `domain=${domain}; ` : ''}${expire}`
    }
  }
  try {
    localStorage.removeItem(CLE_ORIGINE_ACQUISITION)
  } catch {
    /* noop */
  }
}

/** Appelé par le lien « Gérer mes cookies » du pied de page. */
export function requestReopenConsentBanner(): void {
  if (typeof window === 'undefined') return
  window.dispatchEvent(new Event(CONSENT_REOPEN_EVENT))
}
